import {
    createContext,
    useCallback,
    useContext,
    useState,
    useReducer,
} from 'react';

import { Typography } from '@mui/material';

import { LoaderContext } from './LoaderContext';
import { userAxios } from '../utils/api';
import { logger } from '../utils/SiteHelpers';
import { AlertMessageContext } from './AlertMessageContext';
import Modal from '../components/reusable/modal/Modal';
import { useAuth } from './AuthContext';

export const CartContext = createContext();

const initialState = {
    cart: {},
    loading: false,
    error: null,
};

const ACTIONS = {
    SET_LOADING: 'SET_LOADING',
    SET_ERROR: 'SET_ERROR',
    SET_CART: 'SET_CART',
    ADD_TO_CART: 'ADD_TO_CART',
    REMOVE_FROM_CART: 'REMOVE_FROM_CART',
    UPDATE_CART: 'UPDATE_CART',
    PROCESS_PAYMENT: 'PROCESS_PAYMENT',
    CLEAR_CART: 'CLEAR_CART',
    CLEAR_ERROR: 'CLEAR_ERROR',
};

const cartReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_LOADING:
            return { ...state, loading: action.payload };
        case ACTIONS.SET_ERROR:
            return { ...state, error: action.payload };
        case ACTIONS.SET_CART:
            return { ...state, cart: action.payload };
        case ACTIONS.ADD_TO_CART:
            return {
                ...state,
                cart: {
                    ...state.cart,
                    vendors: (state.cart.vendors || []).map((vendor) =>
                        vendor.vendor.id === action.payload.vendor_id
                            ? {
                                  ...vendor,
                                  items: [...vendor.items, action.payload.item],
                              }
                            : vendor,
                    ),
                },
            };
        case ACTIONS.REMOVE_FROM_CART:
            return {
                ...state,
                cart: {
                    ...state.cart,
                    vendors: state.cart.vendors.map((vendor) => ({
                        ...vendor,
                        items: vendor.items.filter(
                            (item) => item.item_id !== action.payload,
                        ),
                    })),
                },
            };
        case ACTIONS.UPDATE_CART:
            return {
                ...state,
                cart: {
                    ...state.cart,
                    vendors: state.cart.vendors.map((vendor) => ({
                        ...vendor,
                        items: vendor.items.map((item) =>
                            item.item_id === action.payload.item_id
                                ? { ...item, quantity: action.payload.quantity }
                                : item,
                        ),
                    })),
                },
            };
        case ACTIONS.CLEAR_CART:
            return { ...state, cart: { vendors: [] } };
        case ACTIONS.CLEAR_ERROR:
            return { ...state, error: null };
        default:
            return state;
    }
};

export const CartProvider = ({ children }) => {
    const [state, dispatch] = useReducer(cartReducer, initialState);
    const { cart, loading, error } = state;
    const { user, accessToken, userId } = useAuth();
    const { setErrorMessage } = useContext(AlertMessageContext);
    const { startLoading, stopLoading } = useContext(LoaderContext);
    const [openDialog, setOpenDialog] = useState(false);
    const [pendingItem, setPendingItem] = useState(null);

    const emptyCart = async () => {
        startLoading('Updating shopping cart');
        try {
            if (!accessToken) {
                throw new Error('No access token available.');
            }

            const url = 'cart/empty';
            const payload = { user_id: userId };
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            };
            const response = await userAxios.post(url, payload, config);

            if (response.status === 200) {
                dispatch({ type: ACTIONS.CLEAR_CART });
            } else {
                logger.error('Failed to clear cart:', response);
            }
        } catch (error) {
            logger.error('Error clearing cart:', error);
            const errorMessage = 'Failed to clear cart.';
            setErrorMessage(errorMessage);

            dispatch({
                type: ACTIONS.SET_ERROR,
                payload: errorMessage,
            });
        } finally {
            stopLoading();
        }
    };

    const handleConfirmClearCart = async () => {
        if (pendingItem) {
            if (!accessToken) {
                throw new Error('No access token available.');
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            };

            // Empty the cart before adding the new item
            await emptyCart();

            // Add the item after clearing the cart
            const payload = { ...pendingItem, user_id: userId };
            const response = await userAxios.post(`cart/add`, payload, config);

            logger.log('Add to Cart Response:', response.data);

            dispatch({
                type: ACTIONS.ADD_TO_CART,
                payload: {
                    vendor_id: pendingItem.vendor_id,
                    item: pendingItem,
                },
            });

            setPendingItem(null);
        }
        setOpenDialog(false);
    };

    const getCart = useCallback(async () => {
        if (!user) {
            return;
        }
        dispatch({ type: ACTIONS.SET_LOADING, payload: true });
        dispatch({ type: ACTIONS.CLEAR_ERROR });

        try {
            if (!accessToken) {
                throw new Error('No access token available.');
            }

            const payload = {
                user_id: userId,
            };
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            };

            const response = await userAxios.post(`cart/get`, payload, config);

            logger.log('Fetch Cart Response:', response.data);

            dispatch({ type: ACTIONS.SET_CART, payload: response.data });
        } catch (error) {
            logger.error('Fetch Cart Error:', error);

            const errorMessage = 'Failed to load cart.';

            dispatch({
                type: ACTIONS.SET_ERROR,
                payload: errorMessage,
            });
        } finally {
            dispatch({ type: ACTIONS.SET_LOADING, payload: false });
        }
    }, [user, userId, accessToken]);

    const addToCart = useCallback(
        async (item) => {
            startLoading('Updating shopping cart');

            try {
                if (!accessToken) {
                    throw new Error('No access token available.');
                }

                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                };

                // Check if cart has a different vendor
                if (cart?.vendors?.length > 0) {
                    const existingVendorId = cart.vendors[0]?.vendor?.id;
                    if (existingVendorId !== item.vendor_id) {
                        setPendingItem(item);
                        setOpenDialog(true);
                        stopLoading();
                        return;
                    }
                }

                // Now add the new item
                const payload = {
                    ...item,
                    user_id: userId,
                };
                const response = await userAxios.post(
                    `cart/add`,
                    payload,
                    config,
                );

                logger.log('Add to Cart Response:', response.data);

                dispatch({
                    type: ACTIONS.ADD_TO_CART,
                    payload: {
                        vendor_id: item.vendor_id,
                        item: item,
                    },
                });
            } catch (error) {
                logger.error('Add to Cart Error:', error);

                const errorMessage = 'Failed to add item to cart.';
                setErrorMessage(errorMessage);

                dispatch({
                    type: ACTIONS.SET_ERROR,
                    payload: errorMessage,
                });
            } finally {
                stopLoading();
            }
        },
        [cart, accessToken],
    );

    const removeFromCart = useCallback(
        async (item_id) => {
            startLoading('Updating shopping cart');

            try {
                if (!accessToken) {
                    throw new Error('No access token available.');
                }

                const payload = {
                    item_id,
                    user_id: userId,
                };
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                };

                const response = await userAxios.post(
                    `cart/remove`,
                    payload,
                    config,
                );

                logger.log('Remove from Cart Response:', response.data);

                dispatch({ type: ACTIONS.REMOVE_FROM_CART, payload: item_id });
            } catch (error) {
                logger.error('Remove from Cart Error:', error);
                const errorMessage = 'Failed to remove item from cart.';
                setErrorMessage(errorMessage);
            } finally {
                stopLoading();
            }
        },
        [accessToken, userId],
    );

    const updateCart = useCallback(
        async (item_id, quantity) => {
            startLoading('Updating shopping cart');

            try {
                if (!accessToken) {
                    throw new Error('No access token available.');
                }

                const payload = {
                    user_id: userId,
                    item_id,
                    quantity,
                };
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                };

                const response = await userAxios.post(
                    'cart/update',
                    payload,
                    config,
                );

                logger.log('Update Cart Response:', response.data);

                dispatch({
                    type: ACTIONS.UPDATE_CART,
                    payload: { item_id, quantity },
                });
            } catch (error) {
                logger.error('Update Cart Error:', error);
                const errorMessage = 'Failed to update cart.';
                setErrorMessage(errorMessage);
            } finally {
                stopLoading();
            }
        },
        [accessToken, userId],
    );

    const processPayment = useCallback(
        async (cart_id, payment_method) => {
            startLoading('Processing Payment');

            try {
                if (!accessToken) {
                    throw new Error('No access token available.');
                }

                const payload = {
                    user_id: Number(userId),
                    cart_id,
                    payment_method: payment_method,
                };
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                };

                const response = await userAxios.post(
                    'cart/checkout',
                    payload,
                    config,
                );

                logger.log('Payment Response:', response.data);

                if (response.data.error) {
                    setErrorMessage(response.data.message);
                    return;
                }

                window.open(response.data.redirectUrl, '_self');

                dispatch({
                    type: ACTIONS.PROCESS_PAYMENT,
                    payload: response.data,
                });
            } catch (error) {
                logger.error('Payment Error:', error);

                const errorMessage = 'Failed to process payment.';

                setErrorMessage(errorMessage);
            } finally {
                stopLoading();
            }
        },
        [accessToken, userId],
    );

    const cartItemCount = (cart.vendors || []).reduce(
        (count, vendor) =>
            count +
            vendor.items.reduce(
                (vendorCount, item) => vendorCount + item.quantity,
                0,
            ),
        0,
    );

    return (
        <CartContext.Provider
            value={{
                cart,
                loading,
                error,
                getCart,
                addToCart,
                removeFromCart,
                updateCart,
                cartItemCount,
                processPayment,
                emptyCart,
            }}
        >
            {children}

            <Modal
                show={openDialog}
                size="xs"
                title="Clear Cart?"
                modalBody={
                    <Typography>
                        Your cart contains items from a different vendor. Do you
                        want to clear the cart and add the new item?
                    </Typography>
                }
                footerButtons={[
                    {
                        label: 'Confirm',
                        onClick: handleConfirmClearCart,
                    },
                    {
                        label: 'Cancel',
                        variant: 'dark',
                        onClick: () => setOpenDialog(false),
                    },
                ]}
            />
        </CartContext.Provider>
    );
};
